/**
 * Three questions she can answer from the records alone: what is worst right
 * now, what has moved since you were last here, and what is still open as you
 * leave.
 *
 * The options attached to an urgent item are the part to be careful with. She
 * offers them in the platform's own terms — an alert with no work order raised
 * from it is a fact the platform models, not an opinion — and every option
 * carries what it costs as well as what it buys. Options without trade-offs are
 * a recommendation with extra steps, and a recommendation is a decision she is
 * not allowed to make.
 *
 * Ordering is by the platform's severity and priority fields, never by anything
 * this file invents. If an operator disagrees with the order, they are
 * disagreeing with the record, which is the argument worth having.
 */
import type { DataSource } from "../api/dataSource";
import type { Alert } from "../domain/alerts";
import type { WorkOrder } from "../domain/workOrders";
import { isOpen, priorityLabel } from "../domain/workOrders";
import { formatDate } from "../lib/format";
import type { Source, Suggestion } from "./types";

export interface UrgentItem {
  id: string;
  label: string;
  /** The record's own reason for being here. */
  because: string;
  /** Ranking key: 0 is worst. */
  rank: number;
  href?: string;
  /** What could be done, each with its cost. Never one "recommended" option. */
  options: Suggestion[];
}

export interface UrgentReport {
  items: UrgentItem[];
  lines: string[];
  sources: Source[];
}

/*
 * The app's own formatter, not a slice of the ISO string.
 *
 * Slicing takes the UTC calendar date. At Madinah, UTC+3, a work order
 * completed at 01:30 local carries a timestamp of 22:30 the previous day — so
 * the assistant said "completed 2026-09-11" beside a detail page that said
 * "Sep 12, 2026", about the same record, on the same screen. An operator who
 * notices that has just been given a reason to check everything else she says.
 */
const day = (iso: string | undefined) => (iso ? formatDate(iso) : "unknown");

function daysOld(iso: string | undefined, now: number): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor((now - t) / 86_400_000);
}

/**
 * Options for an alert. The branch that matters: whether the platform already
 * has a work order raised from it, because "nothing has been raised from this"
 * is the single most actionable thing she can say about an open alert.
 */
function alertOptions(a: Alert, raised: WorkOrder | undefined): Suggestion[] {
  if (raised) {
    return [
      {
        id: `${a.id}-follow`,
        label: `Follow ${raised.number}, already raised from this alert`,
        tradeOff: `It is ${raised.status}. Watching it costs nothing; it also does not speed it up.`,
      },
      {
        id: `${a.id}-escalate`,
        label: "Raise its priority",
        tradeOff: `It sits at ${priorityLabel(raised.priority)}. Moving it up moves something else down — the queue is the same length either way.`,
      },
    ];
  }
  return [
    {
      id: `${a.id}-raise`,
      label: "Create a new work order against it",
      tradeOff: "Puts it in the queue and on the record. It also commits a crew you may want elsewhere today.",
    },
    {
      id: `${a.id}-ack`,
      label: "Acknowledge and keep watching",
      tradeOff: "Right when the rule is noisy. If it is not noisy, the delay is on the record too.",
    },
  ];
}

function workOrderOptions(w: WorkOrder, old: number | null): Suggestion[] {
  const out: Suggestion[] = [];
  if (w.status === "draft" || w.status === "approved") {
    out.push({
      id: `${w.id}-dispatch`,
      label: "Dispatch it",
      tradeOff: "Starts the clock and takes a crew off whatever they are on.",
    });
  }
  if (w.status === "on_hold") {
    out.push({
      id: `${w.id}-resume`,
      label: "Take it off hold",
      tradeOff: "Only worth doing if what it was held for has actually cleared.",
    });
  }
  out.push({
    id: `${w.id}-open`,
    label: "Open it and read the history",
    tradeOff:
      old !== null && old > 7 ? `It has been open ${old} days — the history is where that went.` : "Costs a minute.",
  });
  return out;
}

export async function urgent(
  source: DataSource,
  siteId: string,
  siteCode: string,
  now: number = Date.now(),
): Promise<UrgentReport> {
  const [wos, alerts] = await Promise.all([
    source.listWorkOrders({ siteId }).catch(() => ({ items: [] as WorkOrder[], truncated: false })),
    source.listAlerts(siteId).catch(() => [] as Alert[]),
  ]);

  const open = wos.items.filter((w) => isOpen(w.status));
  const openAlerts = alerts.filter((a) => a.status === "open");
  const items: UrgentItem[] = [];

  for (const a of [...openAlerts].sort((x, y) => y.severity - x.severity || (x.raisedAt < y.raisedAt ? -1 : 1))) {
    const raised = wos.items.find((w) => w.alertId === a.id);
    items.push({
      id: a.id,
      label: a.title,
      because:
        `Alert · severity ${a.severity}${a.zoneCode ? ` · ${a.zoneCode}` : ""} · raised ${day(a.raisedAt)}` +
        (raised ? ` · ${raised.number} raised from it` : " · no work order raised from it"),
      rank: 10 - a.severity,
      href: "/alerts",
      options: alertOptions(a, raised),
    });
  }

  for (const w of [...open].sort((x, y) => x.priority - y.priority || (x.openedAt < y.openedAt ? -1 : 1))) {
    if (w.priority > 2) continue; // P1 and P2 only; the rest is the queue, not the urgent list
    const old = daysOld(w.openedAt, now);
    items.push({
      id: w.id,
      label: `${w.number} · ${w.title}`,
      because:
        `${priorityLabel(w.priority)} · ${w.status}${w.zoneCode ? ` · ${w.zoneCode}` : ""} · opened ${day(w.openedAt)}` +
        (old !== null && old > 7 ? ` · open ${old} days` : ""),
      rank: 5 + w.priority,
      href: `/work-orders/${w.id}`,
      options: workOrderOptions(w, old),
    });
  }

  items.sort((a, b) => a.rank - b.rank);

  const lines =
    items.length === 0
      ? [`${siteCode}: nothing open at severity or P1–P2. The queue below that is still there.`]
      : [
          `${siteCode}: ${openAlerts.length} open alert${openAlerts.length === 1 ? "" : "s"}, ` +
            `${open.filter((w) => w.priority <= 2).length} work order${open.filter((w) => w.priority <= 2).length === 1 ? "" : "s"} at P1–P2.`,
          "Worst first, by the platform's own severity and priority. What to do about them is yours:",
        ];

  return {
    items: items.slice(0, 5),
    lines,
    sources: [
      { kind: "local", label: `Alerts · ${siteCode}`, detail: `${openAlerts.length} open` },
      { kind: "local", label: `Work orders · ${siteCode}`, detail: `${open.length} open` },
    ],
  };
}

/* ── what has moved since you were last here ──────────────────────────── */

export interface DoneReport {
  lines: string[];
  sources: Source[];
  /** True when there is genuinely nothing to report, so callers can stay quiet. */
  empty: boolean;
}

/**
 * Completions are dated by the platform (`completedAt`, `verifiedAt`), so this
 * is a real window rather than a guess. Alerts are not: the record carries no
 * resolved-at, so she reports the standing count and says that is what it is,
 * instead of implying those all happened while you were away.
 */
export async function doneSince(
  source: DataSource,
  siteId: string,
  siteCode: string,
  since: number | null,
): Promise<DoneReport> {
  if (since === null) return { lines: [], sources: [], empty: true };

  const wos = await source.listWorkOrders({ siteId }).catch(() => ({ items: [] as WorkOrder[], truncated: false }));

  const after = (iso?: string) => {
    if (!iso) return false;
    const t = Date.parse(iso);
    return Number.isFinite(t) && t >= since;
  };

  const completed = wos.items.filter((w) => after(w.completedAt));
  const verified = wos.items.filter((w) => after(w.verifiedAt));
  const lines: string[] = [];

  if (completed.length) {
    lines.push(
      `${completed.length} work order${completed.length === 1 ? "" : "s"} completed: ` +
        completed
          .slice(0, 4)
          .map((w) => w.number)
          .join(", ") +
        (completed.length > 4 ? ", and more" : "") +
        ".",
    );
  }
  if (verified.length) {
    lines.push(
      `${verified.length} verified: ` +
        verified
          .slice(0, 4)
          .map((w) => w.number)
          .join(", ") +
        (verified.length > 4 ? ", and more" : "") +
        ".",
    );
  }

  return {
    lines,
    sources: lines.length ? [{ kind: "local", label: `Work orders · ${siteCode}`, detail: "by completion date" }] : [],
    empty: lines.length === 0,
  };
}

/* ── what is still open as you leave ───────────────────────────────────── */

export async function signOffBrief(source: DataSource, siteId: string, siteCode: string): Promise<string[]> {
  const [wos, alerts] = await Promise.all([
    source.listWorkOrders({ siteId }).catch(() => ({ items: [] as WorkOrder[], truncated: false })),
    source.listAlerts(siteId).catch(() => [] as Alert[]),
  ]);
  const open = wos.items.filter((w) => isOpen(w.status));
  const p1 = open.filter((w) => w.priority === 1);
  const openAlerts = alerts.filter((a) => a.status === "open");
  const worst = openAlerts.reduce((m, a) => Math.max(m, a.severity), 0);

  const lines = [
    `${siteCode} as you leave: ${open.length} work order${open.length === 1 ? "" : "s"} open, ` +
      `${openAlerts.length} alert${openAlerts.length === 1 ? "" : "s"} open.`,
  ];
  if (p1.length)
    lines.push(
      `${p1.length} at P1: ${p1
        .slice(0, 3)
        .map((w) => w.number)
        .join(", ")}.`,
    );
  if (worst >= 4) lines.push(`Highest open severity is ${worst}.`);
  lines.push("It carries to the next shift, not to nobody.");
  return lines;
}

/* ── the status of one work order ─────────────────────────────────────── */

export interface StatusReport {
  lines: string[];
  sources: Source[];
  options: Suggestion[];
  found: boolean;
}

/**
 * "Where is WO-000012?"
 *
 * The useful answer is not the status field. It is the status field plus how
 * long it has sat there, who has it, and what the log says happened last —
 * because "in progress" is reassuring right up until you learn it has been in
 * progress for eleven days.
 *
 * When the number does not exist she says so plainly and stops. The failure
 * that matters here is answering about a *different* work order because the
 * reference was close; a miss is recoverable, a confident mismatch is not.
 */
export async function statusOf(
  source: DataSource,
  siteId: string,
  siteCode: string,
  ref: string,
  now: number = Date.now(),
): Promise<StatusReport> {
  const wos = await source.listWorkOrders({ siteId }).catch(() => ({ items: [] as WorkOrder[], truncated: false }));

  const wanted = ref.toUpperCase().replace(/\s+/g, "");
  const digits = wanted.replace(/^WO-?0*/, "");
  const wo =
    wos.items.find((w) => w.number.toUpperCase() === wanted) ??
    wos.items.find((w) => w.number.toUpperCase().replace(/^WO-?0*/, "") === digits);

  if (!wo) {
    return {
      found: false,
      lines: [`No work order numbered ${ref} at ${siteCode}. I have not guessed at a near match.`],
      sources: [{ kind: "local", label: `Work orders · ${siteCode}`, detail: `${wos.items.length} searched` }],
      options: [],
    };
  }

  const old = daysOld(wo.openedAt, now);
  const inState = daysOld(wo.startedAt ?? wo.openedAt, now);
  const lines = [
    `${wo.number} · ${wo.title}`,
    `${priorityLabel(wo.priority)} · ${wo.status}${wo.zoneCode ? ` · ${wo.zoneCode}` : ""}${wo.assetTag ? ` · ${wo.assetTag}` : ""}`,
    `Opened ${day(wo.openedAt)}${old !== null ? ` — ${old} day${old === 1 ? "" : "s"} ago` : ""}.`,
  ];
  if (wo.crew) lines.push(`With ${wo.crew}.`);
  if (wo.dueAt) lines.push(`Due ${day(wo.dueAt)}.`);
  if (wo.startedAt && !wo.completedAt && inState !== null && inState > 3) {
    lines.push(`It has been ${wo.status} for ${inState} days.`);
  }
  if (wo.completedAt) lines.push(`Completed ${day(wo.completedAt)}.`);
  if (wo.verifiedAt) lines.push(`Verified ${day(wo.verifiedAt)}.`);
  if (wo.alertId) lines.push("Raised from an alert.");

  /* The transition log is the platform's own account of what happened; a
     status without it is a snapshot with the reasons filed off. */
  const history = await source.getWorkOrderHistory(wo.id).catch(() => []);
  const last = history[history.length - 1];
  if (last) {
    lines.push(`Last move: ${last.from ?? "—"} → ${last.to} by ${last.by ?? "unknown"} on ${day(last.at)}.`);
    if (last.note) lines.push(`Their note: “${last.note}”`);
  }

  return {
    found: true,
    lines,
    sources: [
      { kind: "local", label: `Work orders · ${siteCode}`, detail: wo.number },
      ...(history.length
        ? [{ kind: "local" as const, label: "Transition log", detail: `${history.length} entries` }]
        : []),
    ],
    options: isOpen(wo.status) ? workOrderOptions(wo, old) : [],
  };
}
